import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_API_KEYS_RESOURCE } from 'src/features/apiKeys/api/useListApiKeys';
import { useUpdateApiKey } from 'src/features/apiKeys/api/useUpdateApiKey';
import ApiKeyFormModal from 'src/features/apiKeys/components/forms/ApiKeyFormModal';
import { apiKeysMessages } from 'src/features/apiKeys/utils/apiKeysMessages';
import { ApiKey } from 'src/features/apiKeys/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateApiKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedApiKey: ApiKey;
    children?: React.ReactNode;
}

function UpdateApiKeyFormModal<TData>({ className, children, selectedApiKey }: UpdateApiKeyFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const dialogTitle = `${formatMessage(generalMessages.edit)} ${formatMessage(apiKeysMessages.apiKey)}`;

    const { mutate, isPending } = useUpdateApiKey({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_API_KEYS_RESOURCE] });
            toast({
                title: formatMessage(apiKeysMessages.updateApiKeySuccess),
                description: formatMessage(apiKeysMessages.updateApiKeySuccessDescription, {
                    entry: success?.name,
                }),
            });
        },
    });

    return (
        <ApiKeyFormModal
            selectedApiKey={selectedApiKey}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, keyId: selectedApiKey.id, parkingId }, { onSuccess, onError })
            }
            title={dialogTitle}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(apiKeysMessages.editApiKeySubtitle, {
                entry: selectedApiKey?.name,
            })}
        >
            {children}
        </ApiKeyFormModal>
    );
}

export default UpdateApiKeyFormModal;
